// @ts-nocheck
/**
 * 行動実行モジュール
 * 攻撃、スキル、防御などの行動を実行
 * @module twinKey/core/battle/ActionExecutor
 */

import { calculateDamage, getEffectivenessText } from './DamageCalculator';
import { executeSkill, getSkillInfo } from './SkillExecutor';

// ============================================================
// ActionExecutor クラス
// ============================================================

export class ActionExecutor {
  /**
   * @param {Object} state - バトル状態への参照
   */
  constructor(state) {
    this.state = state;
  }

  /**
   * 行動を実行
   * @param {Object} actor - 行動するユニット
   * @param {Object} action - 行動内容 { type, targetId?, skillId? }
   * @returns {{ success: boolean, type: string, logs: Array, results: Array }}
   */
  execute(actor, action) {
    if (!actor || !actor.isAlive) {
      return {
        success: false,
        type: 'error',
        logs: [{ type: 'error', message: '行動不能' }],
        results: [],
      };
    }

    switch (action.type) {
      case 'attack':
        return this.executeAttack(actor, action.targetId);

      case 'skill':
        return this.executeSkillAction(actor, action.skillId, action.targetId);

      case 'defend':
        return this.executeDefend(actor);

      case 'skip':
        return this.executeSkip(actor);

      default:
        return {
          success: false,
          type: 'error',
          logs: [{ type: 'error', message: '不明な行動タイプ' }],
          results: [],
        };
    }
  }

  /**
   * 通常攻撃を実行
   * @param {Object} attacker - 攻撃者
   * @param {string} targetId - ターゲットID
   * @returns {Object} 実行結果
   */
  executeAttack(attacker, targetId) {
    const target = this.findUnit(targetId);

    if (!target || !target.isAlive) {
      return {
        success: false,
        type: 'attack',
        logs: [{ type: 'error', message: '有効なターゲットがいません' }],
        results: [],
      };
    }

    // ダメージ計算
    const result = calculateDamage(attacker, target, 1.0);

    // ダメージ適用
    target.stats.currentHp = Math.max(0, target.stats.currentHp - result.damage);

    // ログ作成
    let message = `${attacker.name}の攻撃！${target.name}に${result.damage}ダメージ！`;
    if (result.isCritical) {
      message += '（クリティカル！）';
    }
    const effectivenessText = getEffectivenessText(result.effectiveness);
    if (effectivenessText) {
      message += ` ${effectivenessText}`;
    }

    const logs = [{
      type: result.isCritical ? 'critical' : 'damage',
      message,
      actorId: attacker.id,
      targetId: target.id,
      damage: result.damage,
    }];

    // 装備によるライフスティール
    if (result.lifesteal > 0 && result.damage > 0) {
      const healAmount = Math.floor(result.damage * result.lifesteal);
      if (healAmount > 0) {
        attacker.stats.currentHp = Math.min(attacker.stats.maxHp, attacker.stats.currentHp + healAmount);
        logs.push({
          type: 'heal',
          message: `${attacker.name}はHPを${healAmount}吸収！`,
          actorId: attacker.id,
          amount: healAmount,
        });
      }
    }

    // 撃破判定
    if (target.stats.currentHp <= 0) {
      target.isAlive = false;
      logs.push({
        type: 'defeat',
        message: `${target.name}を撃破！`,
        targetId: target.id,
      });
    }

    return {
      success: true,
      type: 'attack',
      actorId: attacker.id,
      logs,
      results: [{
        targetId: target.id,
        damage: result.damage,
        isCritical: result.isCritical,
        effectiveness: result.effectiveness,
        defeated: !target.isAlive,
      }],
    };
  }

  /**
   * スキル行動を実行
   * @param {Object} user - 使用者
   * @param {string} skillId - スキルID
   * @param {string|string[]} targetId - ターゲットID（全体攻撃の場合は配列）
   * @returns {Object} 実行結果
   */
  executeSkillAction(user, skillId, targetId) {
    // スキル取得
    const skill = user.skills.find(s => s.id === skillId);
    if (!skill) {
      return {
        success: false,
        type: 'skill',
        logs: [{ type: 'error', message: 'スキルが存在しません' }],
        results: [],
      };
    }

    // ターゲット取得
    let targets;
    if (skill.type === 'aoe') {
      // 全体攻撃：敵または味方全員
      targets = user.isAlly
        ? this.state.enemies.filter(e => e.isAlive)
        : this.state.allies.filter(a => a.isAlive);
    } else {
      // 単体攻撃
      const target = this.findUnit(targetId);
      if (!target || !target.isAlive) {
        return {
          success: false,
          type: 'skill',
          logs: [{ type: 'error', message: '有効なターゲットがいません' }],
          results: [],
        };
      }
      targets = target;
    }

    // スキル実行
    const skillResult = executeSkill(user, skill, targets, this.state);

    // 撃破判定
    const defeatedLogs = [];
    const targetArray = Array.isArray(targets) ? targets : [targets];
    for (const target of targetArray) {
      if (target.stats.currentHp <= 0 && target.isAlive) {
        target.isAlive = false;
        defeatedLogs.push({
          type: 'defeat',
          message: `${target.name}を撃破！`,
          targetId: target.id,
        });
      }
    }

    return {
      success: skillResult.success,
      type: 'skill',
      actorId: user.id,
      skillId,
      skillName: skill.name,
      logs: [...skillResult.logs, ...defeatedLogs],
      results: skillResult.results.map(r => ({
        ...r,
        defeated: !targetArray.find(t => t.id === r.targetId)?.isAlive,
      })),
    };
  }

  /**
   * 防御を実行
   * @param {Object} actor - 行動者
   * @returns {Object} 実行結果
   */
  executeDefend(actor) {
    actor.isDefending = true;

    return {
      success: true,
      type: 'defend',
      logs: [{
        type: 'defend',
        message: `${actor.name}は防御態勢をとった！（被ダメージ軽減）`,
        actorId: actor.id,
      }],
      results: [{
        actorId: actor.id,
        isDefending: true,
      }],
    };
  }

  /**
   * 行動スキップ
   * @param {Object} actor - 行動者
   * @returns {Object} 実行結果
   */
  executeSkip(actor) {
    return {
      success: true,
      type: 'skip',
      logs: [{
        type: 'skip',
        message: `${actor.name}は行動をスキップした`,
        actorId: actor.id,
      }],
      results: [],
    };
  }

  /**
   * ユニットを検索
   * @param {string} unitId - ユニットID
   * @returns {Object | null}
   */
  findUnit(unitId) {
    const allUnits = [...this.state.allies, ...this.state.enemies];
    return allUnits.find(u => u.id === unitId) || null;
  }

  /**
   * 行動可能な選択肢を取得
   * @param {Object} actor - 行動者
   * @returns {Array} 行動選択肢
   */
  getAvailableActions(actor) {
    if (!actor || !actor.isAlive) {
      return [];
    }

    const actions = [];

    // 攻撃
    actions.push({
      type: 'attack',
      name: '攻撃',
      description: '通常攻撃を行う',
      needsTarget: true,
    });

    // 使用可能なスキル
    for (const skill of actor.skills) {
      const info = getSkillInfo(skill, actor);
      if (info.canUse) {
        actions.push({
          type: 'skill',
          skillId: skill.id,
          name: skill.name,
          description: `${info.typeText}攻撃 / 倍率: ×${info.multiplier}`,
          needsTarget: skill.type !== 'aoe',
        });
      }
    }

    // 防御
    actions.push({
      type: 'defend',
      name: '防御',
      description: 'このターンの被ダメージを軽減',
      needsTarget: false,
    });

    return actions;
  }

  /**
   * 有効なターゲットを取得
   * @param {Object} actor - 行動者
   * @param {string} actionType - 行動タイプ
   * @returns {Array} ターゲット候補
   */
  getValidTargets(actor, actionType) {
    if (actionType === 'defend') {
      return [];
    }

    // 攻撃系は敵が対象
    if (actor.isAlly) {
      return this.state.enemies.filter(e => e.isAlive);
    } else {
      return this.state.allies.filter(a => a.isAlive);
    }
  }
}
